/**
 * FERP Portal - Page-Level Error Boundary
 *
 * Catches React rendering errors in page components and displays
 * a graceful fallback instead of a blank screen or crash.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Must be a class component (React error boundaries require it)
 */

import React from 'react';
import { Container, Alert, Button, Stack, Text, Code } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

interface PageErrorBoundaryProps {
  children: React.ReactNode;
  pageName?: string;
}

interface PageErrorBoundaryState {
  error: Error | null;
}

export class PageErrorBoundary extends React.Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  state: PageErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { error };
  }

  render() {
    if (this.state.error) {
      const pageName = this.props.pageName ?? 'This page';
      return (
        <Container size="md" py="xl">
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="red"
            title={`${pageName} failed to load`}
          >
            <Stack gap="sm">
              <Text size="sm">
                An unexpected error occurred while rendering this page.
              </Text>
              <Code block>{this.state.error.message}</Code>
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="light"
                size="sm"
                onClick={() => this.setState({ error: null })}
                style={{ alignSelf: 'flex-start' }}
              >
                Try Again
              </Button>
            </Stack>
          </Alert>
        </Container>
      );
    }
    return this.props.children;
  }
}
